import type { ConnectionCategory, NativeToolEffect } from '@parcel/tools';
import type { SpecToolDecision } from '@parcel/types';
import type { AppLogoName } from '@parcel/ui/app-logo';
import type { ConnectionCardStatus } from '../components/settings/connection-card.js';
import { apiFetch } from './api.js';

/**
 * Typed client for the Connections page. Wire shapes live in
 * apps/api/src/routes/spec-connections.ts; mappers name fields explicitly (Ruling 1).
 */

// ---------------------------------------------------------------------------
// Client types
// ---------------------------------------------------------------------------

export type ConnectionScopingMode = 'workspace' | 'member';
export type ConnectionStatus = 'authorizing' | 'connected' | 'error' | 'disabled' | 'revoked';

/** The curated fields a connector detail view renders for one tool. Never widen this with a
    spread -- see toCatalogEntryToolSummary, which this type exists to keep honest. */
export interface ConnectionCatalogToolSummary {
  key: string;
  providerSlug: string;
  description: string;
  effect: NativeToolEffect;
  defaultPolicy: SpecToolDecision;
  registryFloor: SpecToolDecision;
}

export interface ConnectionCatalogEntrySummary {
  key: string;
  name: string;
  description: string;
  icon: string;
  categories: readonly ConnectionCategory[];
  scopingModes: readonly ConnectionScopingMode[];
  /** Policy can never resolve below this on a shared workspace connection. */
  sharedPolicyFloor: SpecToolDecision;
  /** OAuth scopes requested at consent; empty means the provider discloses none. */
  requiredScopes: readonly string[];
  /** Plain-language disclosure of what requiredScopes grants; only set for a broad grant. */
  accessNote?: string;
  tools: readonly ConnectionCatalogToolSummary[];
}

export interface ConnectionSummary {
  id: string;
  scopingMode: ConnectionScopingMode;
  upstreamAccountLabel: string | null;
  status: ConnectionStatus;
  errorCode: string | null;
  createdAt: string;
  lastConnectedAt: string | null;
}

export interface WorkspaceConnectionEntry {
  catalogKey: string;
  connection: ConnectionSummary | null;
}

export interface CreateConnectionInput {
  catalogKey: string;
  scopingMode: ConnectionScopingMode;
}

export interface CreateConnectionResult {
  connection: ConnectionSummary;
  redirectUrl: string;
}

/** No credential field of any kind: a custom server's auth is discovered, never configured. */
export interface CreateCustomConnectionInput {
  name: string;
  url: string;
  /** Sent only after the member has seen and accepted the unauthenticated-server warning. */
  acknowledgeUnauthenticated?: boolean;
}

export interface CreateCustomConnectionResult {
  connection: ConnectionSummary;
  /** Null when the server asked for no authorization, so there is nowhere to send the browser. */
  redirectUrl: string | null;
}

// ---------------------------------------------------------------------------
// Wire shapes (apps/api/src/routes/spec-connections.ts)
// ---------------------------------------------------------------------------

interface ConnectionWire {
  id: string;
  scopingMode: string;
  upstreamAccountLabel: string | null;
  status: string;
  errorCode: string | null;
  createdAt: string;
  lastConnectedAt: string | null;
}

interface CatalogToolWire {
  key: string;
  providerSlug: string;
  description: string;
  effect: string;
  defaultPolicy: string;
  registryFloor: string;
}

interface CatalogEntryWire {
  key: string;
  name: string;
  description: string;
  icon: string;
  categories?: string[];
  scopingModes: string[];
  sharedPolicyFloor: string;
  requiredScopes?: string[];
  accessNote?: string;
  tools: CatalogToolWire[];
}

interface ConnectionListEntryWire {
  catalogKey: string;
  connection: ConnectionWire | null;
}

function toConnectionSummary(wire: ConnectionWire): ConnectionSummary {
  return {
    id: wire.id,
    scopingMode: wire.scopingMode as ConnectionScopingMode,
    upstreamAccountLabel: wire.upstreamAccountLabel,
    status: wire.status as ConnectionStatus,
    errorCode: wire.errorCode,
    createdAt: wire.createdAt,
    lastConnectedAt: wire.lastConnectedAt,
  };
}

// Named field by field, never spread: `tools` is the catalog response's own nested array, and
// a spread here would carry a poisoned entry's tool-level secret straight to the client.
function toCatalogEntryToolSummary(wire: CatalogToolWire): ConnectionCatalogToolSummary {
  return {
    key: wire.key,
    providerSlug: wire.providerSlug,
    description: wire.description,
    effect: wire.effect as NativeToolEffect,
    defaultPolicy: wire.defaultPolicy as SpecToolDecision,
    registryFloor: wire.registryFloor as SpecToolDecision,
  };
}

function toCatalogEntrySummary(wire: CatalogEntryWire): ConnectionCatalogEntrySummary {
  return {
    key: wire.key,
    name: wire.name,
    description: wire.description,
    icon: wire.icon,
    // Defaulted, not trusted: a stale bundle talking to a newer API (or the reverse) must
    // render the connector uncategorised, never throw on a missing array.
    categories: Array.isArray(wire.categories) ? (wire.categories as ConnectionCategory[]) : [],
    scopingModes: wire.scopingModes as ConnectionScopingMode[],
    sharedPolicyFloor: wire.sharedPolicyFloor as SpecToolDecision,
    requiredScopes: Array.isArray(wire.requiredScopes) ? wire.requiredScopes : [],
    accessNote: wire.accessNote,
    tools: wire.tools.map(toCatalogEntryToolSummary),
  };
}

// ---------------------------------------------------------------------------
// Reads and writes
// ---------------------------------------------------------------------------

export async function listConnectionCatalog(
  workspaceId: string,
): Promise<ConnectionCatalogEntrySummary[]> {
  const wire = await apiFetch<{ catalog: CatalogEntryWire[] }>(
    `/workspaces/${workspaceId}/spec/connections/catalog`,
  );
  return wire.catalog.map(toCatalogEntrySummary);
}

export async function listWorkspaceConnections(
  workspaceId: string,
): Promise<WorkspaceConnectionEntry[]> {
  const wire = await apiFetch<{ connections: ConnectionListEntryWire[] }>(
    `/workspaces/${workspaceId}/spec/connections`,
  );
  return wire.connections.map((entry) => ({
    catalogKey: entry.catalogKey,
    connection: entry.connection ? toConnectionSummary(entry.connection) : null,
  }));
}

/** memberId is never sent: the server always derives a member connection's owner from the actor. */
export async function createConnection(
  workspaceId: string,
  input: CreateConnectionInput,
): Promise<CreateConnectionResult> {
  const wire = await apiFetch<{ connection: ConnectionWire; redirectUrl: string }>(
    `/workspaces/${workspaceId}/spec/connections`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return { connection: toConnectionSummary(wire.connection), redirectUrl: wire.redirectUrl };
}

/** Owner/admin only. Always workspace-scoped in this release, so no scoping mode is sent. */
export async function createCustomConnection(
  workspaceId: string,
  input: CreateCustomConnectionInput,
): Promise<CreateCustomConnectionResult> {
  const wire = await apiFetch<{ connection: ConnectionWire; redirectUrl: string | null }>(
    `/workspaces/${workspaceId}/spec/connections/custom`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return { connection: toConnectionSummary(wire.connection), redirectUrl: wire.redirectUrl };
}

/** Owner/admin only. Re-runs discovery against the connection's own stored endpoint, so the
    catalog key, the display name and every tool classification survive. */
export async function reconnectCustomConnection(
  workspaceId: string,
  connectionId: string,
): Promise<CreateCustomConnectionResult> {
  const wire = await apiFetch<{ connection: ConnectionWire; redirectUrl: string | null }>(
    `/workspaces/${workspaceId}/spec/connections/${connectionId}/reconnect`,
    { method: 'POST' },
  );
  return { connection: toConnectionSummary(wire.connection), redirectUrl: wire.redirectUrl };
}

export async function deleteConnection(workspaceId: string, connectionId: string): Promise<void> {
  await apiFetch<void>(`/workspaces/${workspaceId}/spec/connections/${connectionId}`, {
    method: 'DELETE',
  });
}

/** Isolated so a test can mock this one navigation call; jsdom's own Location will not redefine. */
/**
 * Sends the member to the provider's consent screen in a new tab.
 *
 * Not a same-tab navigation: consent happens on the provider's site and ends on a callback
 * URL that is not this app, so replacing the tab threw the member out of Parcel to finish a
 * side errand. Falls back to this tab when the browser refuses the popup, which is still
 * better than a consent screen that never opens.
 */
export function followConnectionRedirect(redirectUrl: string): void {
  // Open a blank tab first so a non-null handle still means the popup was
  // allowed. Passing `noopener` to window.open makes the browser return null
  // even after it successfully opens the tab, which cannot drive a fallback.
  const opened = window.open('', '_blank');
  if (!opened) {
    window.location.assign(redirectUrl);
    return;
  }
  opened.opener = null;
  opened.location.replace(redirectUrl);
}

// ---------------------------------------------------------------------------
// Authorization mirror (Ruling 3): not a boundary -- the API re-checks every write.
// ---------------------------------------------------------------------------

export function canConnectAsWorkspace(
  entry: Pick<ConnectionCatalogEntrySummary, 'scopingModes'>,
  canManageConnections: boolean,
): boolean {
  return entry.scopingModes.includes('workspace') && canManageConnections;
}

export function canConnectAsMember(
  entry: Pick<ConnectionCatalogEntrySummary, 'scopingModes'>,
): boolean {
  return entry.scopingModes.includes('member');
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

// UTC, matching every other calendar date in this app (see spec-tool-row.ts).
const CONNECTION_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

export const CONNECTION_STATUS_LABELS: Readonly<Record<ConnectionStatus, string>> = {
  authorizing: 'Authorizing',
  connected: 'Connected',
  error: 'Needs attention',
  disabled: 'Disabled',
  revoked: 'Revoked',
};

/**
 * Error codes worth a sentence. Everything else shows the raw code, which is
 * what an operator needs for a failure nobody has written copy for yet.
 */
const CONNECTION_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  AUTHORIZATION_ABANDONED: 'Sign-in was never finished. Reconnect to try again.',
  INITIALIZING_TIMEOUT: 'The provider did not finish setting this up. Reconnect to try again.',
  EXPIRED: 'Access expired. Reconnect to restore it.',
  FAILED: 'The provider rejected this connection. Reconnect to try again.',
};

export function describeConnectionError(errorCode: string | null | undefined): string {
  if (!errorCode) return 'Something went wrong with this connection.';
  return CONNECTION_ERROR_MESSAGES[errorCode] ?? `Error: ${errorCode}`;
}

/** Maps the domain status to connection-card.tsx's own, decoupled status vocabulary. */
export function toConnectionCardStatus(status: ConnectionStatus): ConnectionCardStatus {
  if (status === 'authorizing') return 'pending';
  if (status === 'connected') return 'active';
  if (status === 'error') return 'error';
  return 'inactive';
}

export function scopingModeLabel(mode: ConnectionScopingMode): string {
  return mode === 'workspace' ? 'Workspace' : 'Personal';
}

/** Which scopes an entry can ever be connected under, for the detail view's Details block. */
export function describeScopingModes(modes: readonly ConnectionScopingMode[]): string {
  const workspace = modes.includes('workspace');
  const member = modes.includes('member');
  if (workspace && member) return 'Workspace or personal';
  if (workspace) return 'Workspace only';
  if (member) return 'Personal only';
  return 'Not available';
}

/** OAuth scopes requested at consent, for the detail view's Details block. */
export function describeRequiredScopes(scopes: readonly string[]): string {
  return scopes.length > 0 ? scopes.join(', ') : 'Not specified by provider';
}

/** A member connection is always the viewer's own; a workspace one names no individual creator. */
export function describeConnectionOwner(
  connection: Pick<ConnectionSummary, 'scopingMode' | 'createdAt'> | null,
): string | null {
  if (!connection) return null;
  const date = CONNECTION_DATE_FORMAT.format(new Date(connection.createdAt));
  return connection.scopingMode === 'member'
    ? `Connected by you on ${date}`
    : `Shared with the workspace on ${date}`;
}

export function describeLastActive(
  connection: Pick<ConnectionSummary, 'lastConnectedAt'> | null,
): string | null {
  if (!connection?.lastConnectedAt) return null;
  return `Last active ${CONNECTION_DATE_FORMAT.format(new Date(connection.lastConnectedAt))}`;
}

// A Map, not an object literal: an icon named e.g. 'toString' must miss, never resolve
// to an inherited Object.prototype method that then crashes logoUrl() downstream.
const CATALOG_ICON_DOMAINS: ReadonlyMap<string, string> = new Map([
  ['hubspot', 'hubspot.com'],
  ['microsoft_teams', 'teams.microsoft.com'],
  ['outlook', 'outlook.com'],
  ['notion', 'notion.so'],
  ['asana', 'asana.com'],
  ['slack', 'slack.com'],
  ['salesforce', 'salesforce.com'],
]);

export function catalogEntryLogoDomain(icon: string): string | null {
  return CATALOG_ICON_DOMAINS.get(icon) ?? null;
}

// Bundled SVGs beat logo.dev: no round trip, no fallback flash. Callers try this map first;
// catalogEntryLogoDomain above is the fallback for icons with no file in app-logos/ (e.g. asana).
const CATALOG_ICON_APP_LOGOS: ReadonlyMap<string, AppLogoName> = new Map([
  ['hubspot', 'hubspot'],
  ['microsoft_teams', 'microsoft-teams'],
  ['outlook', 'microsoft-outlook'],
  ['notion', 'notion'],
  ['slack', 'slack'],
  ['salesforce', 'salesforce'],
]);

export function catalogEntryAppLogo(icon: string): AppLogoName | null {
  return CATALOG_ICON_APP_LOGOS.get(icon) ?? null;
}
